/**
 * Join code, QR code and share actions. SPEC.md §6.1, §6.2, §19, §20.4, §33.
 *
 * Collapsed by default, because it is read once at the start of the night and then
 * never again — and while it was expanded it was the tallest thing on the lobby.
 * The *code* stays visible without expanding: that is the part someone reads out
 * across the table, and hiding it behind a tap would be hiding the one thing this
 * card is for. §6.1
 *
 * The disclosure is a real one — a button carrying aria-expanded and aria-controls
 * over a panel that stays in the DOM (so the id is never dangling) and is `inert`
 * while closed, so nothing collapsed can be tabbed into. The open animation is a
 * grid-row transition well under 250 ms, and the global reduced-motion rule in
 * index.css switches it off. §19, §20.4
 *
 * The QR is generated on the device, and only once the panel has actually been
 * opened. A remote image service is doubly wrong here: the CSP allows no
 * third-party origin (img-src is 'self' plus data:), and a table in a pub basement
 * may have no usable signal at the exact moment someone is trying to join. §6.1,
 * §39, §44
 *
 * The code, its display form and the URL all come from the shared join-code
 * helpers so a dictated code, a scanned code and a typed code are the same
 * string. §6.2
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatJoinCode, joinUrl } from '@card-counter/shared';
import { Button } from '@/components/ui/Button';
import { haptic } from '@/lib/haptics';
import { useSessionInfo } from '@/stores/session-store';

/** How long the copy confirmation stays up. Long enough to read, short enough to forget. */
const CONFIRMATION_MS = 2_400;

/** Rendered at 512 px and scaled down by CSS so it stays sharp on any density. */
const QR_PIXELS = 512;

export function ShareCard() {
  const session = useSessionInfo();
  const joinCode = session?.joinCode ?? null;

  const [expanded, setExpanded] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const confirmationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = useId();

  const url = useMemo(
    () => (joinCode ? joinUrl(window.location.origin, joinCode) : null),
    [joinCode],
  );

  useEffect(() => {
    // Nothing is encoded for a card nobody has opened: the encoder is a sizeable
    // dependency and most nights it is never needed at all. §44
    if (!url || !expanded) return;
    let cancelled = false;
    void (async () => {
      try {
        const { toDataURL } = await import('qrcode');
        // Fixed black on white regardless of theme: a themed QR is a QR that some
        // scanners refuse. §6.1
        const dataUrl = await toDataURL(url, {
          errorCorrectionLevel: 'M',
          margin: 1,
          width: QR_PIXELS,
          color: { dark: '#000000', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch {
        // No canvas (or an oversized payload): the code and the link below are
        // still enough to join, so this degrades rather than fails.
        if (!cancelled) setQrDataUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, expanded]);

  useEffect(
    () => () => {
      if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    },
    [],
  );

  if (!session || !joinCode || !url) return null;

  const display = formatJoinCode(joinCode);
  // The protocol is noise on a card someone is reading out. §6.1
  const shortUrl = url.replace(/^https?:\/\//, '');
  const canShare = typeof navigator.share === 'function';

  const confirmCopy = (): void => {
    setCopied(true);
    setCopyFailed(false);
    if (confirmationTimer.current) clearTimeout(confirmationTimer.current);
    confirmationTimer.current = setTimeout(() => {
      setCopied(false);
    }, CONFIRMATION_MS);
  };

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      haptic('commit');
      confirmCopy();
    } catch {
      // Insecure context, or permission refused: the link is selectable below.
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const share = async (): Promise<void> => {
    try {
      await navigator.share({ title: 'Card Counter', text: `Join my table ${display}`, url });
    } catch {
      // Dismissing the share sheet rejects; that is not a failure.
    }
  };

  return (
    <section
      aria-label="Invite players"
      className="flex flex-col items-center gap-2 rounded-card border border-line bg-surface p-3 text-center shadow-card"
    >
      <p className="text-xs font-semibold tracking-[0.2em] text-ink-faint uppercase">Game</p>
      <p
        data-numeric
        className="text-3xl font-bold tracking-[0.18em] text-ink select-all"
        // Read out as characters where possible: "C A T - 2 8 4", not "cat dash 284".
        aria-label={display.split('').join(' ')}
      >
        {display}
      </p>

      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => {
          setExpanded((open) => !open);
        }}
        className="flex min-h-tap w-full items-center justify-center gap-2 rounded-control border border-line bg-raised px-3 text-sm font-semibold text-ink"
      >
        {expanded ? 'Hide QR code' : 'Invite players'}
        <span
          aria-hidden="true"
          className={`text-xs transition-transform duration-200 ease-out-soft ${
            expanded ? 'rotate-180' : ''
          }`}
        >
          ▾
        </span>
      </button>

      {/*
        Kept in the DOM so aria-controls always resolves, and animated by row size
        rather than height so it never needs a measured pixel value. §19, §20.4
      */}
      <div
        id={panelId}
        inert={!expanded}
        className="grid w-full transition-[grid-template-rows] duration-200 ease-out-soft"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="flex min-h-0 flex-col items-center gap-3 overflow-hidden">
          {qrDataUrl ? (
            <div className="mt-1 rounded-card bg-white p-2">
              <img
                src={qrDataUrl}
                alt=""
                aria-hidden="true"
                width={QR_PIXELS}
                height={QR_PIXELS}
                className="block size-[min(48vw,180px)]"
              />
            </div>
          ) : null}

          <p className="text-sm text-ink-muted">Scan to join</p>
          <p className="w-full text-sm break-all text-ink select-all">{shortUrl}</p>

          <div className="flex w-full flex-wrap justify-center gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => {
                void copyLink();
              }}
            >
              {copied ? 'Copied ✓' : 'Copy link'}
            </Button>
            {canShare ? (
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  void share();
                }}
              >
                Share
              </Button>
            ) : null}
          </div>

          <p role="status" aria-live="polite" className="min-h-4 text-xs text-ink-muted">
            {copied ? 'Link copied to the clipboard.' : null}
            {copyFailed ? 'Press and hold the link above to copy it.' : null}
          </p>
        </div>
      </div>
    </section>
  );
}
